import React, { useEffect, useState, useRef } from 'react';
import { Camera, Video, MapPin, Trash2, Upload, Check, Crosshair, MapPinned, Users, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiUpload } from '../lib/api';
import { enqueue, cachedGet, remainingPhotoSlots } from '../lib/offlineQueue';
import OfflineReadyBadge from './OfflineReadyBadge';
import ContactPicker from './crm/ContactPicker';
import ContactFormDrawer from './crm/ContactFormDrawer';
import SubcontractorPicker from './SubcontractorPicker';
import EmployeePicker from './EmployeePicker';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';

/**
 * Reusable Site Visit Report form renderer.
 * Used by both desktop SiteVisitReport page and mobile MobileSiteVisit page.
 *
 * Props:
 *   enquiryId        string
 *   onSaved          (svr) => void
 *   compact          boolean — slimmer paddings + label sizes for mobile
 */
export default function SVRForm({ enquiryId, onSaved, compact = false, onHasSvr }) {
  const ff = useFormFieldHelpers('svr_create');
  const [enquiry, setEnquiry] = useState(null);
  const [template, setTemplate] = useState([]);
  const [svr, setSvr] = useState({
    sections: {}, gps_lat: null, gps_lng: null, gps_accuracy_m: null,
    photos: [], videos: [],
    visited_by_employee_ids: [],
    client_contact_id: '',
    nearby_supplier_ids: [],
  });
  const [employeeIndex, setEmployeeIndex] = useState({}); // id -> {name, emp_id, designation}
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [contactPrefill, setContactPrefill] = useState({});
  const [contactRefresh, setContactRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  const [hasSvr, setHasSvr] = useState(false);  // true once SVR has been saved at least once
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.location.pathname.startsWith('/m');

  // Load template + existing SVR. On mobile these reads are cache-then-network
  // so the form opens (with the last-seen data) even with no signal.
  useEffect(() => {
    const get = isMobile
      ? (key, path) => cachedGet(key, path).then((r) => r.data)
      : (key, path) => apiGet(path);
    Promise.all([
      get('svr-template', '/enquiries/svr-template'),
      get(`enq:${enquiryId}`, `/enquiries/${enquiryId}`),
      get(`svr:${enquiryId}`, `/enquiries/${enquiryId}/site-visit-report`),
      // Tendering_28Sep2026 §2: use /employees/lookup (any auth user) instead of /hr/employees (Tendering → 403)
      // Tendering_30Sep2026 §1: cap at 100 (the endpoint's max) — 200 returned a 422.
      get('employees', '/employees/lookup?limit=100').then(d => d.items || d || []).catch(() => []),
    ]).then(([tpl, enq, svrRes, emps]) => {
      setTemplate(tpl.sections || []);
      setEnquiry(enq.enquiry);
      const idx = {};
      (Array.isArray(emps) ? emps : []).forEach((e) => {
        idx[e.id] = { id: e.id, name: e.name, emp_id: e.emp_id, designation: e.designation, department: e.department };
      });
      setEmployeeIndex(idx);
      const existing = svrRes.site_visit_report;
      if (existing) {
        setHasSvr(true);
        onHasSvr?.(true);
        setSvr({
          sections: existing.sections || {},
          gps_lat: existing.gps_lat ?? null,
          gps_lng: existing.gps_lng ?? null,
          gps_accuracy_m: existing.gps_accuracy_m ?? null,
          photos: existing.photos || [],
          videos: existing.videos || [],
          visited_by_employee_ids: existing.visited_by_employee_ids || [],
          client_contact_id: existing.client_contact_id || existing.site_visit_contact_id || '',
          nearby_supplier_ids: existing.nearby_supplier_ids || [],
          nearby_supplier_names: existing.nearby_supplier_names || [],
        });
      } else {
        // New SVR: default Date of visit to today (editable)
        const today = new Date().toISOString().slice(0, 10);
        // Tendering_30Sep2026 §12: pre-fill water/electricity supply scope from
        // the enquiry when already known there — confirmed/corrected on site.
        const accessLogistics = {};
        if (enq.enquiry?.water_supply_scope) accessLogistics.water_supply_scope = enq.enquiry.water_supply_scope;
        if (enq.enquiry?.electricity_supply_scope) accessLogistics.electricity_supply_scope = enq.enquiry.electricity_supply_scope;
        if (enq.enquiry?.water_chargeable_by_client) accessLogistics.water_chargeable_by_client = true;
        if (enq.enquiry?.electricity_chargeable_by_client) accessLogistics.electricity_chargeable_by_client = true;
        setSvr((s) => ({
          ...s,
          sections: {
            ...s.sections,
            general_info: { ...(s.sections.general_info || {}), date_of_visit: today },
            access_logistics: { ...(s.sections.access_logistics || {}), ...accessLogistics },
          },
        }));
      }
    }).catch(() => toast.error('Could not load SVR'));
  }, [enquiryId]);

  const updField = (secKey, fldKey, val) => {
    setSvr((s) => ({
      ...s,
      sections: { ...s.sections, [secKey]: { ...(s.sections[secKey] || {}), [fldKey]: val } },
    }));
  };

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error('Geolocation is not supported by this browser'); return; }
    setGettingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSvr((s) => ({
          ...s,
          gps_lat: pos.coords.latitude,
          gps_lng: pos.coords.longitude,
          gps_accuracy_m: pos.coords.accuracy,
        }));
        toast.success(`GPS captured · ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} (±${Math.round(pos.coords.accuracy)}m)`, { duration: 5000 });
        setGettingGps(false);
      },
      (err) => {
        const map = {
          1: 'Permission denied. Allow location access in your browser and try again.',
          2: 'Position unavailable — GPS signal weak. Try moving to an open area.',
          3: 'Location request timed out. Try again.',
        };
        toast.error(map[err.code] || `GPS failed: ${err.message}`, { duration: 7000 });
        setGettingGps(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const [linkInput, setLinkInput] = useState('');
  const [resolvingLink, setResolvingLink] = useState(false);
  const resolveLink = async () => {
    const url = (linkInput || '').trim();
    if (!url) { toast.error('Paste a Maps link or "lat,lng" first'); return; }
    setResolvingLink(true);
    try {
      const r = await apiPost('/settings/resolve-location', { url });
      setSvr((s) => ({ ...s, gps_lat: r.lat, gps_lng: r.lng, gps_accuracy_m: null }));
      setLinkInput('');
      toast.success(`Location set: ${r.lat.toFixed(6)}, ${r.lng.toFixed(6)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resolve link', { duration: 9000 });
    } finally { setResolvingLink(false); }
  };

  const uploadFile = async (file, kind) => {
    const offline = isMobile && typeof navigator !== 'undefined' && !navigator.onLine;
    if (offline) {
      if (kind !== 'photo') { toast.error('Videos can’t be saved offline — add them after you reconnect.'); return; }
      const remaining = await remainingPhotoSlots();
      const offlineCount = (svr.photos || []).filter((p) => p.offline).length;
      if (remaining - offlineCount < 1) { toast.error('Offline storage full (20 photos) — sync pending entries first.'); return; }
      try {
        const dataUrl = await compressImage(file);
        setSvr((s) => ({
          ...s,
          photos: [...(s.photos || []), {
            id: `offline-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            dataUrl, original_filename: file.name, kind: 'photo', offline: true,
          }],
        }));
        toast.success('Photo saved offline');
      } catch { toast.error('Could not process photo'); }
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiUpload(`/enquiries/${enquiryId}/upload-media?kind=${kind}`, fd);
      const field = kind === 'photo' ? 'photos' : 'videos';
      setSvr((s) => ({ ...s, [field]: [...(s[field] || []), res] }));
      toast.success(`${kind === 'photo' ? 'Photo' : 'Video'} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeMedia = (kind, mediaId) => {
    setSvr((s) => ({ ...s, [kind]: s[kind].filter((m) => m.id !== mediaId) }));
    // soft-remove on backend best-effort
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${enquiryId}/media/${mediaId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
    }).catch(() => {});
  };

  const save = async () => {
    setSaving(true);
    const uploadedPhotos = (svr.photos || []).filter((p) => !p.offline);
    const offlinePhotos = (svr.photos || []).filter((p) => p.offline);
    const basePayload = {
      sections: svr.sections,
      gps_lat: svr.gps_lat, gps_lng: svr.gps_lng, gps_accuracy_m: svr.gps_accuracy_m,
      photos: uploadedPhotos, videos: svr.videos,
      visited_by_employee_ids: svr.visited_by_employee_ids || [],
      client_contact_id: svr.client_contact_id || null,
      nearby_supplier_ids: svr.nearby_supplier_ids || [],
      visit_date: new Date().toISOString(),
    };

    const queueOffline = async () => {
      if (offlinePhotos.length) {
        const remaining = await remainingPhotoSlots();
        if (offlinePhotos.length > remaining) {
          toast.error(`Only ${remaining} offline photo slot(s) left — sync pending entries first.`);
          return false;
        }
      }
      await enqueue('site_visit', {
        payload: { enquiry_id: enquiryId, svr: basePayload },
        photos: offlinePhotos.map((p) => ({ dataUrl: p.dataUrl, name: p.original_filename || 'svr-photo.jpg' })),
      });
      toast.success('Saved offline — will sync when you reconnect');
      onSaved?.(basePayload);
      return true;
    };

    try {
      if (isMobile && typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOffline();
        setSaving(false);
        return;
      }
      const res = await apiPost(`/enquiries/${enquiryId}/site-visit-report`, basePayload);
      toast.success('Site Visit Report saved');
      setHasSvr(true);
      onHasSvr?.(true);
      onSaved?.(res.site_visit_report);
    } catch (e) {
      if (isMobile && !e?.response && await queueOffline()) { setSaving(false); return; }
      toast.error(e?.response?.data?.detail || 'Could not save SVR');
    } finally { setSaving(false); }
  };

  const API = typeof process !== 'undefined' ? process.env.REACT_APP_BACKEND_URL : '';

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/api/enquiries/${enquiryId}/site-visit-report/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SVR_${enquiryId}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  if (!enquiry) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const labelCls = compact ? 'text-[10px] font-display font-bold uppercase tracking-wider text-slate-600' : 'text-[11px] font-display font-bold uppercase tracking-wider text-slate-600';
  const inputCls = `w-full border border-slate-300 rounded-sm ${compact ? 'px-2 py-1.5 text-[13px]' : 'px-2.5 py-2 text-sm'} mt-1 focus:outline-none focus:border-[#3b4872]`;

  return (
    <div className="space-y-4">
      {/* Auto-populated context banner */}
      <div className="bg-[#3b4872]/5 border border-[#3b4872]/20 rounded-sm p-3 text-xs">
        <div className="grid grid-cols-2 gap-2">
          <KV label="Project" value={enquiry.project_name} />
          <KV label="Client" value={enquiry.client} />
          <KV label="Enquiry No" value={enquiry.enquiry_no} mono />
          <KV label="Visit Date" value={(() => {
            const d = svr.sections?.general_info?.date_of_visit;
            if (!d) return '—';
            const dt = new Date(d + 'T00:00:00');
            return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
          })()} />
        </div>
      </div>

      {/* Render each section */}
      {template.map((sec) => (
        <section key={sec.key} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`svr-sec-${sec.key}`}>
          <h3 className="font-display font-bold text-sm uppercase tracking-wide text-[#0F172A] mb-1">{sec.label}</h3>
          {sec.hint && <p className="text-[11px] text-slate-500 mb-3 italic">{sec.hint}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sec.fields.map((f) => {
              // Tendering_30Sep2026 §12: demolition/tree-cutting notes only show
              // when their paired Yes/No sibling field is answered "Yes".
              if (f.show_if && svr.sections[sec.key]?.[f.show_if] !== 'Yes') return null;
              const val = svr.sections[sec.key]?.[f.key];
              const colSpan = (
                f.type === 'textarea' ||
                f.type === 'media' ||
                f.type === 'gps' ||
                f.type === 'supplier_picker' ||
                f.type === 'employee_multi_picker' ||
                f.type === 'client_contact_picker' ||
                f.type === 'contact_picker'
              ) ? 'md:col-span-2' : '';

              if (f.type === 'employee_multi_picker') {
                const ids = svr.visited_by_employee_ids || [];
                const addEmp = (_name, id, emp) => {
                  if (!id || ids.includes(id)) return;
                  setEmployeeIndex((idx) => emp ? { ...idx, [id]: emp } : idx);
                  setSvr((s) => ({ ...s, visited_by_employee_ids: [...(s.visited_by_employee_ids || []), id] }));
                };
                const removeEmp = (id) => {
                  setSvr((s) => ({ ...s, visited_by_employee_ids: (s.visited_by_employee_ids || []).filter((x) => x !== id) }));
                };
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <div className="mt-1 space-y-2">
                      {ids.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="svr-visited-by-chips">
                          {ids.map((id) => {
                            const emp = employeeIndex[id] || { name: id };
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#3b4872]/10 text-[#3b4872] rounded-sm text-xs font-medium border border-[#3b4872]/20"
                                data-testid={`svr-visited-by-chip-${id}`}
                              >
                                <Users size={11} />
                                <span>{emp.name || id}</span>
                                {emp.emp_id && <span className="text-[10px] text-[#3b4872]/70 font-mono">{emp.emp_id}</span>}
                                <button
                                  type="button"
                                  onClick={() => removeEmp(id)}
                                  className="ml-0.5 hover:text-rose-600"
                                  data-testid={`svr-visited-by-remove-${id}`}
                                >
                                  <X size={11} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <EmployeePicker
                        key={`emp-picker-${ids.length}`}
                        value=""
                        onChange={addEmp}
                        remoteSearch
                        placeholder={ids.length ? 'Add another team member…' : 'Search employee by name or emp ID…'}
                        allowFreeText={false}
                        testid="svr-visited-by-picker"
                      />
                      <p className="text-[10px] text-slate-500">Multi-select: add each Ellora employee who attended the site visit.</p>
                    </div>
                  </div>
                );
              }

              if (f.type === 'client_contact_picker') {
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <div className="mt-1">
                      <ContactPicker
                        value={svr.client_contact_id}
                        onChange={(id) => setSvr((s) => ({ ...s, client_contact_id: id }))}
                        onAddNew={(q) => { setContactPrefill({ name: q }); setContactDrawerOpen(true); }}
                        refreshSignal={contactRefresh}
                        placeholder="Search BD contact or add new…"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Whoever from the client's side hosted us at the site.</p>
                  </div>
                );
              }

              if (f.type === 'contact_picker') {
                // Legacy field type — older SVR templates still reference it.
                // Kept rendering so historical records do not blow up. New
                // SVR_TEMPLATE uses `employee_multi_picker` + `client_contact_picker`.
                if (!ff.showField('visited_by')) return null;
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{ff.labelOf('visited_by', f.label)}{ff.reqStar('visited_by', false)}</label>
                    <div className="mt-1">
                      <ContactPicker
                        value={svr.client_contact_id}
                        onChange={(id) => setSvr((s) => ({ ...s, client_contact_id: id }))}
                        onAddNew={(q) => { setContactPrefill({ name: q }); setContactDrawerOpen(true); }}
                        refreshSignal={contactRefresh}
                        placeholder="Search contact or add new…"
                      />
                    </div>
                  </div>
                );
              }
              if (f.type === 'supplier_picker') {
                // supplier_picker = vendor details — hidden from SVR (managed in Vendor Master)
                return null;
              }
              if (f.type === 'gps') {
                if (!ff.showField('geofence_lat')) return null;
                const gpsRequired = ff.isReq('geofence_lat', false);
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{ff.labelOf('geofence_lat', f.label)}{gpsRequired && <span className="text-rose-500 ml-0.5">*</span>}</label>
                    <div className="mt-1 bg-emerald-50/40 border border-emerald-200 rounded-sm p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] text-slate-600 leading-snug">
                          Capture the site's GPS so this report is geo-stamped and can be plotted on the map.
                        </p>
                        <MapPinned size={14} className="text-emerald-700 shrink-0" />
                      </div>

                      {/* Primary CTA — one-tap GPS for engineers on-site */}
                      <button type="button" onClick={captureGps} disabled={gettingGps}
                        className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-emerald-300 text-emerald-800 text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="svr-capture-gps">
                        <Crosshair size={13} /> {gettingGps ? 'Getting location…' : 'Use my current location'}
                      </button>

                      <div className="text-[9px] text-center uppercase tracking-widest text-slate-400 mb-2">— OR —</div>

                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <input
                          type="text"
                          value={linkInput}
                          onChange={(e) => setLinkInput(e.target.value)}
                          placeholder='Paste Google Maps link (https://maps.app.goo.gl/...) or "lat,lng"'
                          className="flex-1 min-w-[200px] border border-slate-300 rounded-sm px-2.5 py-2 text-xs focus:outline-none focus:border-[#3b4872]"
                          data-testid="svr-gps-link-input"
                        />
                        <button type="button" onClick={resolveLink} disabled={resolvingLink || !linkInput}
                          className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="svr-gps-link-resolve">
                          {resolvingLink ? 'Resolving…' : 'Use Link'}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-snug">
                        Tip: open Google Maps → tap the <strong>business pin</strong> → <strong>Share → Copy link</strong>. Plain search URLs without a selected pin don't carry coordinates.
                      </p>

                      <div className="mt-2 pt-2 border-t border-emerald-200 flex items-center gap-2">
                        <MapPin size={12} className="text-emerald-700" />
                        {svr.gps_lat && svr.gps_lng ? (
                          <span className="text-xs font-mono text-emerald-700" data-testid="svr-gps-display">
                            {svr.gps_lat.toFixed(6)}, {svr.gps_lng.toFixed(6)} {svr.gps_accuracy_m ? `· ±${Math.round(svr.gps_accuracy_m)}m` : ''}
                          </span>
                        ) : <span className="text-xs text-slate-400">No GPS captured yet</span>}
                      </div>
                    </div>
                  </div>
                );
              }
              if (f.type === 'media') {
                if (!ff.showField('photos')) return null;
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{ff.labelOf('photos', f.label)}{ff.reqStar('photos', false)}</label>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (!files.length) return;
                          let done = 0;
                          for (const file of files) {
                            await uploadFile(file, 'photo');
                            done++;
                            if (files.length > 1) toast(`Uploading photos: ${done}/${files.length}`, { id: 'svr-upload-progress' });
                          }
                          if (files.length > 1) { toast.dismiss('svr-upload-progress'); toast.success(`${files.length} photos uploaded`); }
                          e.target.value = '';
                        }}
                        data-testid="svr-photo-input" />
                      <input ref={videoInputRef} type="file" accept="video/*" capture="environment" className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'video')} data-testid="svr-video-input" />
                      <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploading}
                        className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 rounded-sm hover:border-[#3b4872] flex items-center gap-1.5"
                        title="Click to select one or multiple photos at once"
                        data-testid="svr-add-photo">
                        <Camera size={13} /> Photo (multi-select)
                      </button>
                      <button type="button" onClick={() => videoInputRef.current?.click()} disabled={uploading}
                        className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 rounded-sm hover:border-[#3b4872] flex items-center gap-1.5"
                        data-testid="svr-add-video">
                        <Video size={13} /> Video
                      </button>
                      {uploading && <span className="text-xs text-slate-500 flex items-center gap-1"><Upload size={11} className="animate-pulse" /> Uploading…</span>}
                    </div>
                    {/* Thumbnails */}
                    {svr.photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="svr-photo-grid">
                        {svr.photos.map((p) => {
                          const token = localStorage.getItem('access_token') || '';
                          const serveUrl = p.offline
                            ? p.dataUrl
                            : (p.serve_url ? `${process.env.REACT_APP_BACKEND_URL}${p.serve_url}?auth=${encodeURIComponent(token)}` : p.url);
                          return (
                            <div key={p.id} className="relative group border border-slate-200 rounded-sm overflow-hidden aspect-square bg-slate-100">
                              <img src={serveUrl} alt={p.original_filename} className="w-full h-full object-cover" />
                              {p.offline && <span className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-white text-[8px] font-display font-bold uppercase tracking-wider text-center py-0.5">Offline</span>}
                              <button type="button" onClick={() => removeMedia('photos', p.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`svr-photo-del-${p.id}`}>
                                <Trash2 size={11} className="text-rose-600" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {svr.videos.length > 0 && (
                      <div className="mt-3 space-y-1" data-testid="svr-video-list">
                        {svr.videos.map((v) => (
                          <div key={v.id} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1.5 rounded-sm">
                            <span className="flex items-center gap-1.5"><Video size={11} /> {v.original_filename} <span className="text-slate-400">· {(v.size / 1024 / 1024).toFixed(1)} MB</span></span>
                            <button type="button" onClick={() => removeMedia('videos', v.id)} className="text-rose-600" data-testid={`svr-video-del-${v.id}`}><Trash2 size={11} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (f.type === 'textarea') {
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <textarea value={val || ''} onChange={(e) => updField(sec.key, f.key, e.target.value)} rows={3} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`} />
                  </div>
                );
              }
              if (f.type === 'supply_scope') {
                // Who provides the utility (Contractor/Client/Shared) + whether
                // the client charges us for it when they supply. Both stored in
                // the same section dict so award_enquiry carries them forward.
                const chargeable = !!svr.sections[sec.key]?.[f.chargeable_key];
                const showChargeable = val === 'Client' || val === 'Shared';
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <select value={val || ''} onChange={(e) => {
                      const next = e.target.value;
                      updField(sec.key, f.key, next);
                      // Clear the stale chargeable flag when the utility is no
                      // longer client-provided (scope → '' or 'Contractor').
                      if (next !== 'Client' && next !== 'Shared') updField(sec.key, f.chargeable_key, false);
                    }} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`}>
                      <option value="">— Select —</option>
                      {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {showChargeable && (
                      <label className="mt-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`svr-${sec.key}-${f.chargeable_key}-label`}>
                        <input
                          type="checkbox"
                          checked={chargeable}
                          onChange={(e) => updField(sec.key, f.chargeable_key, e.target.checked)}
                          className="h-4 w-4 rounded-sm border-slate-300 text-[#3b4872] focus:ring-[#3b4872]"
                          data-testid={`svr-${sec.key}-${f.chargeable_key}`}
                        />
                        {f.chargeable_label || 'Chargeable by client?'}
                      </label>
                    )}
                  </div>
                );
              }
              if (f.type === 'select') {
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <select value={val || ''} onChange={(e) => updField(sec.key, f.key, e.target.value)} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`}>
                      <option value="">— Select —</option>
                      {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                );
              }
              if (f.type === 'yesno') {
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <div className="mt-1 flex items-center gap-3">
                      {['Yes', 'No', 'N/A'].map((opt) => (
                        <label key={opt} className="flex items-center gap-1 text-sm">
                          <input type="radio" name={`${sec.key}-${f.key}`} checked={val === opt} onChange={() => updField(sec.key, f.key, opt)} data-testid={`svr-${sec.key}-${f.key}-${opt.toLowerCase()}`} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              }
              if (f.type === 'number') {
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <input type="number" step="any" value={val ?? ''} onChange={(e) => updField(sec.key, f.key, e.target.value)} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`} />
                  </div>
                );
              }
              if (f.type === 'date') {
                const wd = val ? new Date(val + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' }) : '';
                return (
                  <div key={f.key} className={colSpan}>
                    <label className={labelCls}>{f.label}</label>
                    <input type="date" value={val || ''} onChange={(e) => updField(sec.key, f.key, e.target.value)} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`} />
                    {wd && <span className="text-[11px] text-slate-500 mt-1 inline-block" data-testid={`svr-${sec.key}-${f.key}-weekday`}>{wd}</span>}
                  </div>
                );
              }
              // default: text
              return (
                <div key={f.key} className={colSpan}>
                  <label className={labelCls}>{f.label}</label>
                  <input type="text" value={val || ''} onChange={(e) => updField(sec.key, f.key, e.target.value)} className={inputCls} data-testid={`svr-${sec.key}-${f.key}`} />
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {/* Save bar */}
      <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-slate-200 flex items-center justify-end gap-2 z-10">
        {isMobile && <OfflineReadyBadge className="mr-auto" />}
        {hasSvr && (
          <button type="button" onClick={downloadPdf}
            className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50 flex items-center gap-1.5"
            data-testid="svr-download-pdf-btn">
            <Download size={13} /> Download PDF
          </button>
        )}
        <button type="button" onClick={save} disabled={saving}
          className="px-5 py-2 text-xs font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="svr-save-btn">
          <Check size={13} /> {saving ? 'Saving…' : 'Save Site Visit Report'}
        </button>
      </div>

      <ContactFormDrawer
        open={contactDrawerOpen}
        onClose={() => setContactDrawerOpen(false)}
        onCreated={(c) => { setSvr((s) => ({ ...s, client_contact_id: c.id })); setContactRefresh((r) => r + 1); }}
        prefill={contactPrefill}
      />
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xs font-display font-semibold text-slate-900 ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

/** Compress an image File to a JPEG data URL (~70% quality, max 1280px edge)
 *  so offline SVR photos stay small (200–400 KB) in IndexedDB. */
function compressImage(file, maxEdge = 1280, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxEdge || height > maxEdge) {
          if (width >= height) { height = Math.round(height * (maxEdge / width)); width = maxEdge; }
          else { width = Math.round(width * (maxEdge / height)); height = maxEdge; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}